// ─────────────────────────────────────────────────────────────────────────────
// REQ-109 — Widget de racha gamificado de "Brillamos Juntos".
//
// FASE 7 (paridad demo, H-91) — unificado sobre la familia `.streak-*`/`.badge-*` del demo
// (`ShineTogetherHomePage.tsx`, la implementación MÁS COMPLETA de las 3 que había: `.bj-streak*`
// del hero de Brillamos, esta familia `.streak-*` de la Home, y la tercera inventada por el
// integrado `.bj-streak-widget*`, ahora retirada por completo — ver MAPA-CLASES.md).
//
// Datos REALES (useMyStreak → GET v1/streaks/me). SIN puntos (regla de oro): solo semanas de
// racha, récord, total de nominaciones enviadas, actividad de la semana e insignias de progresión.
// Complementario: si el endpoint de racha aún no responde, el widget no se renderiza (no rompe la
// landing).
//
// RF-038 (S1, 2026-08-05) — las insignias se pintan desde `streak.catalogoInsignias`, el catálogo
// que sirve el backend: nombre localizado y umbral vienen de ahí, así que renombrar una insignia
// en Gestión se ve aquí sin tocar código. Antes había un array `BADGES` literal cuyos umbrales
// podían contradecir —y contradecían— los que el backend otorgaba.
// D-S1-9 (2026-08-06) — el degradado y el icono TAMBIÉN reflejan lo configurado en Gestión
// (`color`/`iconUrl`) cuando existen; ver badgeSkins.tsx para el porqué y el cálculo. Solo cuando
// falta `color` cae en la presentación local por ÍNDICE (`BADGE_SKINS`), nunca por nombre ni por
// código: el catálogo es editable y un fallback por nombre se rompería al renombrar. Si el catálogo
// trae más niveles que la paleta de respaldo, el índice cicla.
//
// Estrella SVG (punto 10): el demo usa el emoji de estrella como icono de título y de día activo
// en esta familia `.streak-*`, pero el encargo de esta fase pide sustituirlo por la estrella
// dibujada a mano que SÍ trae el demo en la OTRA familia (`.bj-streak-star`,
// `BrillamosJuntosPage.tsx` : 203-206) — mismo `<path>` exacto, con `--streak-color`/
// `--streak-light` como custom properties. Se aplica también a la insignia "Estrella Naciente"
// (4 semanas) en vez de su emoji de estrella literal.
// Criterio de aceptación de esta fase: cero apariciones del carácter de estrella (unicode U+2B50)
// en este archivo — verificar con grep antes de cerrar la fase.
//
// Días de la semana: se pintan los 7 (L-D) que trae `StreakDto.actividadSemanal`, con sábado y
// domingo atenuados (`sdc--weekend`), igual que el demo.
//
// RF-041 (S4) — la tira de 7 días ya no vive aquí: se extrajo a `StreakWeekStrip`
// (`StreakTracker.tsx`) para que las pantallas sin widget puedan montarla sin copiarla. Este
// componente la consume; el marcado y las clases son los mismos.
// RF-043 (S4) — el widget no se pinta con la racha apagada desde Gestión (ver `useVisibleStreak`).
// ─────────────────────────────────────────────────────────────────────────────
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { getBadgeName } from '@/api/recognition';
import type { BadgeDto } from '@/api/recognition';
import { weeksKeyOf } from '@/utils/weeksPlural';
import { streakUnitCopy } from '@/utils/streakUnit';
import { StreakStar, StreakWeekStrip } from './StreakTracker';
import { useVisibleStreak } from './useVisibleStreak';
import { BadgeIcon, skinFor } from './badgeSkins';
import { useBadgeCelebrationQueue } from './useBadgeCelebrationQueue';
import { BadgeCelebration } from './BadgeCelebration';
import { FloatingTooltip } from '@/components/common/FloatingTooltip';

export function StreakWidget({ programa, isAntiSpoilerOn = false }: Readonly<{ programa?: string | null; isAntiSpoilerOn?: boolean }> = {}) {
  const { t, i18n } = useTranslation('recognition');
  const lang = i18n.language;
  // RF-043 — con la racha apagada en Gestión el widget entero desaparece (racha, tracker e
  // insignias de racha), que es lo que pide el RF: "que NO aparezca en campaña".
  // Rama B — `programa` elige qué racha muestra este widget: la landing de Brillamos pasa 'Brillamos'
  // para ver la racha de Brillamos; sin prop (Perfil/Innovación) es la de Fundamentales, como hoy.
  const s = useVisibleStreak(programa);
  // Se llama SIEMPRE, antes del `if (!s) return null` de abajo — las Rules of Hooks no permiten
  // llamar hooks después de un return condicional. El hook ya maneja `undefined` sin celebrar nada.
  // Celebra tanto insignias GANADAS como PROVISIONALES (RF-036): el widget ya las pinta
  // desbloqueadas desde que el período en curso las alcanza, así que la celebración dispara en el
  // mismo momento, no cuando el cierre semanal real las consolide (ver useBadgeCelebrationQueue.ts).
  const celebration = useBadgeCelebrationQueue(s);
  if (!s) return null;
  // RF-037 — Con periodicidad mensual, `semanasActuales`, `record` y los umbrales de las insignias
  // cuentan MESES. El rótulo tiene que seguir a la periodicidad o el widget miente.
  const unit = streakUnitCopy(s.periodicidad);

  // Catálogo de insignias servido por el backend, ya ordenado por umbral. Las ganadas se cruzan por
  // CÓDIGO contra `insignias` (lo que el usuario tiene), no recalculando el umbral en el cliente:
  // así el widget nunca puede contradecir lo que el backend otorgó.
  // El backend ya sirve solo insignias de racha activas y con código, pero el filtro se repite aquí
  // por lo que significa en pantalla: una insignia sin código no puede cruzarse contra las ganadas
  // ni la otorga el cierre semanal, así que pintarla es prometer un nivel inalcanzable.
  const catalog = (s.catalogoInsignias ?? []).filter((b) => b.code);
  // `||` y no `??`: el backend emite `code: ""` —no null— cuando el documento no lo trae
  // (`MapBadge`), y dos insignias así colapsarían en la misma clave vacía, marcándose ganadas entre
  // ellas. Con `||` cada una cae a su `id`, que sí es único.
  const codeOf = (b: BadgeDto) => b.code || b.id;
  const earnedCodes = new Set((s.insignias ?? []).map(codeOf));
  // RF-036 — Insignias que el récord PROYECTADO alcanza y que el cierre aún no ha otorgado. Se pintan
  // desbloqueadas (si no, la tarjeta mostraría "Racha: 1 semana" junto a "🔒 1 semana") pero con
  // etiqueta propia: no están ganadas y desaparecen si el período deja de calificar. La lista la
  // decide el BACKEND — recalcular el umbral aquí es justo lo que RF-038 prohíbe.
  const provisionalCodes = new Set(s.insigniasProvisionales ?? []);
  const badges = catalog.map((b, i) => ({
    badge: b,
    skin: skinFor(b, i),
    earned: earnedCodes.has(codeOf(b)) || provisionalCodes.has(codeOf(b)),
    provisional: !earnedCodes.has(codeOf(b)) && provisionalCodes.has(codeOf(b)),
  }));
  // Nivel actual = la última ganada del catálogo (el catálogo llega ordenado por umbral).
  const currentLevel = badges.reduce((acc, b, i) => (b.earned ? i : acc), -1);
  // Sin ninguna insignia desbloqueada (ni ganada ni provisional) la fila entera son candados: no
  // aporta nada y ocupa espacio. Se oculta la sección completa hasta la primera insignia real.
  const hasUnlockedBadge = badges.some((b) => b.earned);

  // `nominatedToday` decide el nudge diario contextual de RF-039, no la tira: el mismo giro de
  // índice que hace `StreakWeekStrip` (getDay() es 0=Dom..6=Sáb y `actividadSemanal` va de lunes a
  // domingo).
  const todayIdx = (new Date().getDay() + 6) % 7;
  const nominatedToday = Boolean(s.actividadSemanal[todayIdx]);

  // RF-036 — Progreso del período ABIERTO, tal como lo cuenta el backend (con su tope anti-abuso y
  // el mínimo de la campaña que gobierne a esta persona). No se deriva de `actividadSemanal` ni se
  // recalcula aquí: el widget diría una cosa y el cierre haría otra, que es el defecto que RF-039 ya
  // cerró para el aviso de riesgo.
  // `typeof … === 'number'` y no `??`: los campos son aditivos, y un backend anterior al cambio no
  // los emite. Sustituir la ausencia por 0 pintaría "0 de 1" —"no has enviado nada"— a quien sí ha
  // enviado; ante la ausencia no se pinta la línea.
  const sent = s.enviosPeriodo;
  const min = s.minimoPeriodo;
  const progress = typeof sent === 'number' && typeof min === 'number' && min > 0
    ? { sent, min, done: sent >= min }
    : null;

  // Con Anti-Spoiler activo el chip "Nominaciones" se oculta (no blur, mismo criterio que el resto
  // de la pantalla): se retira del cálculo de la fila de estadísticas para que, si era el único dato
  // con actividad, la fila completa desaparezca en vez de quedar con el hueco vacío del chip retirado.
  const showNominationsStat = s.totalNominaciones > 0 && !isAntiSpoilerOn;

  return (
    <div className="streak-widget shine-card bj-streak-widget">
      <div className="streak-title-row">
        <span className="streak-title-icon">
          <StreakStar color="#a78bfa" light="#f0abfc" />
        </span>
        <div>
          <p className="streak-title-text">{t('streak.title')}</p>
          {/* Subtítulo FIJO, como el prototipo (`shine.streak.subtitle`, demo
              ShineTogetherHomePage.tsx : 285). Antes alternaba entre dos frases propias según la
              actividad de la semana; el mensaje contextual ya lo da el nudge de más abajo. */}
          <p className="streak-title-sub">{t(unit.subtitle)}</p>
        </div>
      </div>

      {/* Un contador en 0 no informa nada ("Récord: 0") y solo resta espacio a los que sí dicen
          algo — se oculta chip por chip. `.streak-stat-chip`/`.streak-fire-block` llevan `flex: 1`
          para que los que queden se repartan la fila en vez de dejar un hueco donde estaba el
          oculto. Si los tres están en 0 (usuario sin ninguna actividad todavía) se oculta la fila
          completa: una tarjeta vacía con solo el borde no aporta nada. */}
      {(s.semanasActuales > 0 || s.record > 0 || showNominationsStat) && (
        <div className="streak-stats-row">
          {s.semanasActuales > 0 && (
            <div className="streak-fire-block">
              <span className="streak-fire" aria-hidden="true">🔥</span>
              <div>
                <p className="streak-count">{s.semanasActuales}<span className="streak-count-label"> {t(weeksKeyOf(s.semanasActuales, unit.countKeys), { count: s.semanasActuales })}</span></p>
                <p className="streak-sublabel">{t('streak.current')}</p>
              </div>
            </div>
          )}
          {s.record > 0 && (
            <div className="streak-stat-chip streak-stat-chip--purple">
              <span className="streak-stat-icon" aria-hidden="true">🏆</span>
              <span className="streak-stat-num">{s.record}</span>
              <p className="streak-sublabel">{t('streak.record')}</p>
            </div>
          )}
          {showNominationsStat && (
            <div className="streak-stat-chip streak-stat-chip--teal">
              <span className="streak-stat-icon" aria-hidden="true">✨</span>
              <span className="streak-stat-num">{s.totalNominaciones}</span>
              <p className="streak-sublabel">{t('streak.total')}</p>
            </div>
          )}
        </div>
      )}
      {/* RF-036 — El contador de arriba YA incluye el período abierto cuando este califica. Se dice,
          en vez de dejar que el número parezca consolidado: puede bajar si se borra un reconocimiento
          y se pierde el mínimo, exactamente como haría el cierre. El flag lo manda el backend; no se
          deduce de `enviosPeriodo >= minimoPeriodo`, porque el freeze también hace contar el período. */}
      {s.periodoEnCursoCuenta && (
        <p className="streak-provisional-note">{t(unit.provisionalNote)}</p>
      )}
      <p className="streak-racha-note">{t('streak.note')}</p>

      <StreakWeekStrip week={s.actividadSemanal} />

      <div className={`streak-nudge ${nominatedToday ? 'streak-nudge--done' : ''}`}>
        <span className="streak-nudge-icon" aria-hidden="true">{nominatedToday ? '🌟' : '🚀'}</span>
        <span>
          {t(nominatedToday ? 'streak.nudgeDone' : 'streak.nudgeTodo')}
          {/* El nudge de arriba es DIARIO (RF-039) y el progreso es del PERÍODO: con periodicidad
              mensual "ya brillaste hoy" convive con "3 de 8 este mes" sin contradecirse. La copia
              sigue a la periodicidad vía `streakUnitCopy`, nunca escribe "semana" a mano. */}
          {progress && (
            <span className="streak-nudge-progress">
              {t(progress.done ? unit.periodProgressDone : unit.periodProgress, {
                sent: progress.sent,
                min: progress.min,
              })}
            </span>
          )}
        </span>
      </div>

      {/* Sin catálogo no se pinta la sección: es preferible a inventar umbrales en el cliente. Y sin
          NINGUNA insignia desbloqueada tampoco: una fila de puros candados no informa nada. */}
      {badges.length > 0 && hasUnlockedBadge && (
        <div className="streak-badges-section">
          <p className="streak-badges-title">{t('streak.badgesTitle')}</p>
          <div className="streak-badges-row">
            {badges.map(({ badge, skin, earned, provisional }, i) => {
              const isCurrent = earned && i === currentLevel;
              const name = getBadgeName(badge, lang);
              const weeks = badge.semanasRequeridas ?? 0;
              // `skin` (calculado arriba con `skinFor`) ya resuelve degradado real vs. respaldo por
              // índice, e `iconUrl` gana sobre el emoji/estrella del nivel cuando existe — ver
              // badgeSkins.tsx (D-S1-9) para el cálculo y el porqué.
              return (
                <div
                  key={badge.id}
                  className={`badge-insignia ${earned ? 'badge--earned' : 'badge--locked'} ${isCurrent ? 'badge--current' : ''} ${provisional ? 'badge--provisional' : ''}`}
                  style={{ '--bg1': skin.g1, '--bg2': skin.g2 } as CSSProperties}
                  title={name}
                >
                  {/* La etiqueta provisional gana a "NIVEL ACTUAL" cuando coinciden: la insignia recién
                      alcanzada es siempre el nivel más alto, y de las dos cosas que se pueden decir en
                      ese hueco, "todavía no es tuya" es la que el usuario no puede deducir.
                      El `title` nativo del div se limita al nombre para no competir con este tooltip
                      propio (ambos a la vez, uno con delay y otro inmediato, se pisaban al hacer hover). */}
                  {provisional && (
                    <FloatingTooltip
                      tooltipClassName="adm-vista-tooltip"
                      content={t('streak.badgeProvisionalTooltip')}
                    >
                      {(anchorProps) => (
                        <button
                          type="button"
                          className="badge-current-tag badge-provisional-tag"
                          {...anchorProps}
                        >
                          {t('streak.badgeProvisional')}
                        </button>
                      )}
                    </FloatingTooltip>
                  )}
                  {isCurrent && !provisional && <span className="badge-current-tag">{t('streak.currentLevel')}</span>}
                  <div className="badge-circle">
                    <div className="badge-circle-inner">
                      <BadgeIcon skin={skin} earned={earned} iconUrl={badge.iconUrl} />
                    </div>
                    {earned && <div className="badge-ring" />}
                  </div>
                  <p className="badge-name">{name}</p>
                  <p className="badge-weeks">{t(weeksKeyOf(weeks, unit.badgeKeys), { count: weeks })}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {s.enRiesgo && !s.congelada && (
        <p className="streak-risk"><i className="fas fa-triangle-exclamation" aria-hidden="true" /> {t(unit.atRisk)}</p>
      )}

      {celebration.current && (
        <BadgeCelebration
          badge={celebration.current}
          skin={skinFor(
            celebration.current,
            Math.max(0, catalog.findIndex((b) => codeOf(b) === codeOf(celebration.current!))),
          )}
          onClose={celebration.dismiss}
        />
      )}
    </div>
  );
}

export default StreakWidget;
